import { Button, Divider, Input, InputNumber, Radio, Select, Space } from "antd/es";
import { useEffect, useState } from "react";
import { BooleanFilterTypes, DateFilterTypes, NumberFilterTypes, StringFilterTypes, ValueType } from "./base-table";
import TriStateSwitch from "./tri-state-switch";

const { Option } = Select;

declare let window: any;

const CustomFilterDropdown = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  dataType,
  visible,
  initialFilters,
  columnId
}) => {
  const [searchType, setSearchType] = useState<
    StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes
  >(
    dataType === ValueType.String
      ? StringFilterTypes.Contains
      : ValueType.Number
      ? NumberFilterTypes.Equals
      : DateFilterTypes.Equals
  );
  const [searchValue, setSearchValue] = useState<string>(selectedKeys?.value);
  const [searchValue2, setSearchValue2] = useState<string>(selectedKeys?.value2);
  const [logicalOperator, setLogicalOperator] = useState<"AND" | "OR">(selectedKeys?.logicalOperator || "AND");

  const [secondSearchType, setSecondSearchType] = useState<
    StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes
  >(selectedKeys?.secondType);
  const [secondSearchValue, setSecondSearchValue] = useState<string>(selectedKeys?.secondValue || "");
  const [secondSearchValue2, setSecondSearchValue2] = useState<string>(selectedKeys?.secondValue2 || "");
  const [showSecondFilter, setShowSecondFilter] = useState<boolean>(
    Boolean(selectedKeys?.secondValue || selectedKeys?.secondType)
  );

  const language = localStorage.getItem("ml");

  const isString = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === StringFilterTypes.Contains ||
      value === StringFilterTypes.DoesNotContain ||
      value === StringFilterTypes.Equals ||
      value === StringFilterTypes.DoesNotEqual ||
      value === StringFilterTypes.BeginsWith ||
      value === StringFilterTypes.EndsWith ||
      value === StringFilterTypes.Blank ||
      value === StringFilterTypes.NotBlank
    ) {
      return true;
    }
    return false;
  };

  const isNumber = (value: NumberFilterTypes | StringFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === NumberFilterTypes.Equals ||
      value === NumberFilterTypes.DoesNotEqual ||
      value === NumberFilterTypes.GreaterThan ||
      value === NumberFilterTypes.GreaterThanOrEqual ||
      value === NumberFilterTypes.LessThan ||
      value === NumberFilterTypes.LessThanOrEqual ||
      value === NumberFilterTypes.Blank ||
      value === NumberFilterTypes.NotBlank ||
      value === NumberFilterTypes.Between
    ) {
      return true;
    }
    return false;
  };

  const isDate = (value: DateFilterTypes | StringFilterTypes | NumberFilterTypes | BooleanFilterTypes) => {
    if (
      value === DateFilterTypes.After ||
      value === DateFilterTypes.Before ||
      value === DateFilterTypes.Between ||
      value === DateFilterTypes.Equals ||
      value === DateFilterTypes.DoesNotEqual ||
      value === DateFilterTypes.Blank ||
      value === DateFilterTypes.NotBlank
    ) {
      return true;
    }
    return false;
  };

  const isBoolean = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    if (
      value === BooleanFilterTypes.Equals ||
      value === BooleanFilterTypes.DoesNotEqual ||
      value === BooleanFilterTypes.Blank ||
      value === BooleanFilterTypes.NotBlank
    ) {
      return true;
    }
    if (dataType === ValueType.Boolean) {
      return true;
    }
    return false;
  };

  const isBlankType = (value: StringFilterTypes | NumberFilterTypes | DateFilterTypes | BooleanFilterTypes) => {
    return (
      value === StringFilterTypes.Blank ||
      value === NumberFilterTypes.Blank ||
      value === DateFilterTypes.Blank ||
      value === BooleanFilterTypes.Blank ||
      value === StringFilterTypes.NotBlank ||
      value === NumberFilterTypes.NotBlank ||
      value === DateFilterTypes.NotBlank ||
      value === BooleanFilterTypes.NotBlank
    );
  };

  useEffect(() => {
    if (searchValue && searchValue.toString().trim() !== "") {
      setShowSecondFilter(true);
    }
  }, [searchValue]);

  useEffect(() => {
    if (isBlankType(searchType)) {
      setShowSecondFilter(true);
    }
  }, [searchType]);

  useEffect(() => {
    if (visible) {
      const searchType = findDataType(dataType);
      const currentType = selectedKeys?.type ?? searchType;
      setSearchType(currentType);
      setSelectedKeys({
        ...selectedKeys,
        type: currentType,
        columnId,
        logicalOperator: selectedKeys?.logicalOperator || logicalOperator,
        secondType: selectedKeys?.secondType,
        secondValue: selectedKeys?.secondValue,
        secondValue2: selectedKeys?.secondValue2
      });

      if (selectedKeys?.secondValue || selectedKeys?.secondType) {
        setShowSecondFilter(true);
        setSecondSearchType(selectedKeys?.secondType || searchType);
        setSecondSearchValue(selectedKeys?.secondValue || "");
        setSecondSearchValue2(selectedKeys?.secondValue2 || "");
      }
    }
  }, [visible]);

  useEffect(() => {
    if (initialFilters && initialFilters.value && initialFilters.type && initialFilters.value !== searchValue) {
      const searchType = findDataType(dataType);
      setSearchType(searchType);
      setSelectedKeys({
        ...selectedKeys,
        value: initialFilters.value,
        type: initialFilters.type,
        columnId,
        logicalOperator: initialFilters.logicalOperator || logicalOperator,
        secondType: initialFilters.secondType,
        secondValue: initialFilters.secondValue,
        secondValue2: initialFilters.secondValue2
      });
      setSearchValue(initialFilters.value);

      if (initialFilters.secondValue || initialFilters.secondType) {
        setShowSecondFilter(true);
        setSecondSearchType(initialFilters.secondType || searchType);
        setSecondSearchValue(initialFilters.secondValue || "");
        setSecondSearchValue2(initialFilters.secondValue2 || "");
      }

      if (initialFilters.logicalOperator) {
        setLogicalOperator(initialFilters.logicalOperator);
      }
      confirm();
    }
  }, []);

  const findDataType = (value: string) => {
    if (value === ValueType.Boolean) {
      return BooleanFilterTypes.Equals;
    }
    if (value === ValueType.String) {
      return StringFilterTypes.Contains;
    }
    if (value === ValueType.Number) {
      return NumberFilterTypes.Equals;
    }
    if (value === ValueType.Date) {
      return DateFilterTypes.Equals;
    }
    return StringFilterTypes.Contains;
  };

  const handleSearch = () => {
    const fullFilter = {
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    };

    setSelectedKeys(fullFilter);
    confirm();
  };

  const handleReset = () => {
    setSelectedKeys("");
    const searchType = findDataType(dataType);
    setSearchType(searchType);
    setSecondSearchType(searchType);
    setSearchValue("");
    setSecondSearchValue("");
    setSecondSearchValue2("");
    setShowSecondFilter(false);
    clearFilters();
    confirm();
  };

  const handleSearchValueChange = (e, secondValue?: boolean) => {
    if (secondValue) {
      const value2 = e.target ? e.target.value : e;
      setSearchValue2(value2);
      setSelectedKeys({
        value: searchValue,
        value2,
        type: searchType,
        columnId,
        logicalOperator,
        secondType: secondSearchType,
        secondValue: secondSearchValue,
        secondValue2: secondSearchValue2
      });
      return;
    }

    const value = e.target ? e.target.value : e;
    setSearchValue(value);

    if (value && value.toString().trim() !== "") {
      setShowSecondFilter(true);
    }

    setSelectedKeys({
      value,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    });
  };

  const handleSecondSearchValueChange = (e, secondValue?: boolean) => {
    if (secondValue) {
      const value2 = e.target ? e.target.value : e;
      setSecondSearchValue2(value2);
      setSelectedKeys({
        value: searchValue,
        value2: searchValue2,
        type: searchType,
        columnId,
        logicalOperator,
        secondType: secondSearchType,
        secondValue: secondSearchValue,
        secondValue2: value2
      });
      return;
    }

    const value = e.target ? e.target.value : e;
    setSecondSearchValue(value);
    setSelectedKeys({
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator,
      secondType: secondSearchType,
      secondValue: value,
      secondValue2: secondSearchValue2
    });
  };

  const handleLogicalOperatorChange = (e) => {
    const newOperator = e.target.value;
    setLogicalOperator(newOperator);
    setSelectedKeys({
      ...selectedKeys,
      value: searchValue,
      value2: searchValue2,
      type: searchType,
      columnId,
      logicalOperator: newOperator,
      secondType: secondSearchType,
      secondValue: secondSearchValue,
      secondValue2: secondSearchValue2
    });
  };

  const isBlank =
    searchType === StringFilterTypes.Blank ||
    searchType === NumberFilterTypes.Blank ||
    searchType === DateFilterTypes.Blank ||
    searchType === BooleanFilterTypes.Blank;
  const isNotBlank =
    searchType === StringFilterTypes.NotBlank ||
    searchType === NumberFilterTypes.NotBlank ||
    searchType === DateFilterTypes.NotBlank ||
    searchType === BooleanFilterTypes.NotBlank;

  const isSecondBlank =
    secondSearchType === StringFilterTypes.Blank ||
    secondSearchType === NumberFilterTypes.Blank ||
    secondSearchType === DateFilterTypes.Blank ||
    secondSearchType === BooleanFilterTypes.Blank;
  const isSecondNotBlank =
    secondSearchType === StringFilterTypes.NotBlank ||
    secondSearchType === NumberFilterTypes.NotBlank ||
    secondSearchType === DateFilterTypes.NotBlank ||
    secondSearchType === BooleanFilterTypes.NotBlank;

  const isStringDataType = dataType === ValueType.String || !dataType;
  const isDesignTime = window.kuika.isDesignTime;

  return (
    <div style={{ padding: 8 }}>
      <Select
        value={searchType}
        style={{ width: "100%", marginBottom: 8, display: "block" }}
        onChange={(value) => {
          setSearchType(value);
          setSelectedKeys({
            value: searchValue,
            value2: searchValue2,
            type: value,
            columnId,
            logicalOperator,
            secondType: secondSearchType,
            secondValue: secondSearchValue,
            secondValue2: secondSearchValue2
          });
        }}
      >
        {isStringDataType && (
          <Option value={StringFilterTypes.Contains}>{language === "tr_TR" ? "İçerir" : "Contains"}</Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.DoesNotContain}>
            {language === "tr_TR" ? "İçermez" : "Does Not Contain"}
          </Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.BeginsWith}>{language === "tr_TR" ? "Başlar" : "Begins With"}</Option>
        )}
        {isStringDataType && (
          <Option value={StringFilterTypes.EndsWith}>{language === "tr_TR" ? "Biter" : "Ends With"}</Option>
        )}
        {isStringDataType && <Option value={StringFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>}
        {isStringDataType && (
          <Option value={StringFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.GreaterThan}>{language === "tr_TR" ? "Büyüktür" : "Greater Than"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.GreaterThanOrEqual}>
            {language === "tr_TR" ? "Büyük Eşittir" : "Greater Than or Equal"}
          </Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.LessThan}>{language === "tr_TR" ? "Küçüktür" : "Less Than"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.LessThanOrEqual}>
            {language === "tr_TR" ? "Küçük Eşittir" : "Less Than or Equal"}
          </Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
        )}
        {dataType === ValueType.Number && (
          <Option value={NumberFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.After}>{language === "tr_TR" ? "Tarihinden Sonra" : "After Date"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Before}>{language === "tr_TR" ? "Tarihinden Önce" : "Before Date"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Between}>
            {language === "tr_TR" ? "Tarihleri Arasında" : "Between Dates"}
          </Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
        )}
        {dataType === ValueType.Date && (
          <Option value={DateFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.DoesNotEqual}>
            {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
          </Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
        )}
        {dataType === ValueType.Boolean && (
          <Option value={BooleanFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
        )}
      </Select>

      {isString(searchType) && !isBlank && !isNotBlank && (
        <Input
          placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
          value={searchValue}
          onChange={(e) => handleSearchValueChange(e)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          autoFocus
          readOnly={isDesignTime}
        />
      )}
      {isNumber(searchType) && !isBlank && !isNotBlank && (
        <InputNumber
          placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
          value={searchValue}
          onChange={(e) => handleSearchValueChange(e)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          autoFocus
          readOnly={isDesignTime}
        />
      )}
      {isDate(searchType) && !isBlank && !isNotBlank && (
        <Input
          type="date"
          placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
          value={searchValue}
          onChange={(e) => handleSearchValueChange(e)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          autoFocus
          readOnly={isDesignTime}
        />
      )}
      {searchType === NumberFilterTypes.Between && (
        <InputNumber
          placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
          value={searchValue2}
          onChange={(e) => handleSearchValueChange(e, true)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          readOnly={isDesignTime}
        />
      )}
      {searchType === DateFilterTypes.Between && (
        <Input
          type="date"
          placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
          value={searchValue2}
          onChange={(e) => handleSearchValueChange(e, true)}
          style={{ width: "100%", marginBottom: 8, display: "block" }}
          autoFocus
        />
      )}
      {isBoolean(searchType) && !isBlank && !isNotBlank && (
        <TriStateSwitch
          value={searchValue || null}
          onChange={(value) => {
            setSearchValue(value);
            setSelectedKeys({
              value,
              type: searchType,
              columnId,
              logicalOperator,
              secondType: secondSearchType,
              secondValue: secondSearchValue,
              secondValue2: secondSearchValue2
            });
          }}
          language={language}
          source={"FilterDropdown"}
        />
      )}

      <Radio.Group
        value={logicalOperator}
        onChange={handleLogicalOperatorChange}
        style={{ width: "100%", marginBottom: 8, display: "flex", justifyContent: "center" }}
      >
        <Radio value="AND" style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginLeft: 8 }}>AND</span>
        </Radio>
        <Radio value="OR" style={{ display: "flex", alignItems: "center" }}>
          <span style={{ marginLeft: 8 }}>OR</span>
        </Radio>
      </Radio.Group>

      {showSecondFilter && (
        <>
          <Divider style={{ margin: "8px 0" }} />

          <Select
            value={secondSearchType}
            style={{ width: "100%", marginBottom: 8, display: "block" }}
            onChange={(value) => {
              setSecondSearchType(value);
              setSelectedKeys({
                value: searchValue,
                value2: searchValue2,
                type: searchType,
                columnId,
                logicalOperator,
                secondType: value,
                secondValue: secondSearchValue,
                secondValue2: secondSearchValue2
              });
            }}
          >
            {isStringDataType && (
              <Option value={StringFilterTypes.Contains}>{language === "tr_TR" ? "İçerir" : "Contains"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.DoesNotContain}>
                {language === "tr_TR" ? "İçermez" : "Does Not Contain"}
              </Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.BeginsWith}>{language === "tr_TR" ? "Başlar" : "Begins With"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.EndsWith}>{language === "tr_TR" ? "Biter" : "Ends With"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {isStringDataType && (
              <Option value={StringFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.GreaterThan}>
                {language === "tr_TR" ? "Büyüktür" : "Greater Than"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.GreaterThanOrEqual}>
                {language === "tr_TR" ? "Büyük Eşittir" : "Greater Than or Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.LessThan}>{language === "tr_TR" ? "Küçüktür" : "Less Than"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.LessThanOrEqual}>
                {language === "tr_TR" ? "Küçük Eşittir" : "Less Than or Equal"}
              </Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Number && (
              <Option value={NumberFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.After}>{language === "tr_TR" ? "Tarihinden Sonra" : "After Date"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Before}>{language === "tr_TR" ? "Tarihinden Önce" : "Before Date"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Between}>
                {language === "tr_TR" ? "Tarihleri Arasında" : "Between Dates"}
              </Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Date && (
              <Option value={DateFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.Equals}>{language === "tr_TR" ? "Eşittir" : "Equals"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.DoesNotEqual}>
                {language === "tr_TR" ? "Eşit Değildir" : "Does Not Equal"}
              </Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.Blank}>{language === "tr_TR" ? "Boş" : "Blank"}</Option>
            )}
            {dataType === ValueType.Boolean && (
              <Option value={BooleanFilterTypes.NotBlank}>{language === "tr_TR" ? "Boş Değildir" : "Not Blank"}</Option>
            )}
          </Select>

          {isString(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <Input
              placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
              value={secondSearchValue}
              onChange={(e) => handleSecondSearchValueChange(e)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {isNumber(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <InputNumber
              placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
              value={secondSearchValue}
              onChange={(e) => handleSecondSearchValueChange(e)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {secondSearchType === NumberFilterTypes.Between && (
            <InputNumber
              placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
              value={secondSearchValue2}
              onChange={(e) => handleSecondSearchValueChange(e, true)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {isDate(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <Input
              type="date"
              placeholder={language === "tr_TR" ? "Filtrele..." : "Filter..."}
              value={secondSearchValue}
              onChange={(e) => handleSecondSearchValueChange(e)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
              readOnly={isDesignTime}
            />
          )}
          {secondSearchType === DateFilterTypes.Between && (
            <Input
              type="date"
              placeholder={language === "tr_TR" ? "İkinci değer..." : "Second value..."}
              value={secondSearchValue2}
              onChange={(e) => handleSecondSearchValueChange(e, true)}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
            />
          )}
          {isBoolean(secondSearchType) && !isSecondBlank && !isSecondNotBlank && (
            <TriStateSwitch
              value={secondSearchValue || null}
              onChange={(value) => {
                setSecondSearchValue(value);
                setSelectedKeys({
                  value: searchValue,
                  value2: searchValue2,
                  type: searchType,
                  columnId,
                  logicalOperator,
                  secondType: secondSearchType,
                  secondValue: value,
                  secondValue2: secondSearchValue2
                });
              }}
              language={language}
              source={"FilterDropdown"}
            />
          )}
        </>
      )}

      <Space style={{ marginTop: 8 }}>
        <Button type="primary" onClick={handleSearch} size="small" style={{ width: 90 }}>
          {language === "tr_TR" ? "Filtrele" : "Filter"}
        </Button>
        <Button onClick={handleReset} size="small" style={{ width: 90 }}>
          {language === "tr_TR" ? "Sıfırla" : "Reset"}
        </Button>
      </Space>
    </div>
  );
};

export default CustomFilterDropdown;
